import React, { useEffect, useMemo, useRef, useState } from "react";
import Map, { Marker } from "react-map-gl";
import { GoogleMap, TrafficLayer, useJsApiLoader } from "@react-google-maps/api";
import "mapbox-gl/dist/mapbox-gl.css";
import { useAlarms } from "../useAlarms";
import { useAgencyInfo } from "./common";
import { collection, db, isDemoData, loadHydrants, onSnapshot, watchRealtime } from "../boardData";
import { DEMO_MAP_TOKEN, demoMapStyle } from "../demo/demoBasemap";
import FloatingMapCard from "../../components/FloatingMapCard/floatingMapCard";
import {
  ALARM_CYCLE_MS,
  GOOGLE_INCIDENT_OPTIONS,
  GOOGLE_INCIDENT_ZOOM,
  GoogleIncidentLayers,
  MAPBOX_INCIDENT_STYLE,
  MAPBOX_INCIDENT_ZOOM,
  MapAnimations,
  MapboxIncidentLayers,
  NoMappingScreen,
  extractCallerCoordinates,
  hasValidCoords,
  nearestHydrants,
} from "../../components/map/incidentMapParts";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const FILL = { width: "100%", height: "100%" };
const UNIT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const IDLE_ZOOM = 15;

const coord = (...values) => {
  for (const v of values) {
    const n = parseFloat(v);
    if (!isNaN(n)) return n;
  }
  return NaN;
};

// Callins (personnel responding) for one alarm, live.
function useCallins(agency, alarmId) {
  const [callins, setCallins] = useState([]);
  useEffect(() => {
    setCallins([]);
    if (!agency || !alarmId) return undefined;
    return onSnapshot(
      collection(db, `${agency} Alarms/${alarmId}/Callins`),
      (snap) => setCallins(snap.docs.map((d) => ({ ...d.data(), qualifications: d.data().qualifications || "" }))),
      () => setCallins([])
    );
  }, [agency, alarmId]);
  return callins;
}

// Units reporting a location in the last 24 hours (Realtime Database).
function useUnitLocations(agency) {
  const [vehicles, setVehicles] = useState([]);
  useEffect(() => {
    if (!agency) return undefined;
    return watchRealtime(`/UnitLocation/${agency}`, (val) => {
      const since = Date.now() - UNIT_MAX_AGE_MS;
      setVehicles(
        Object.keys(val || {})
          .map((key) => ({ responderName: key, agency, ...val[key] }))
          .filter((u) => (u.timestamp || 0) >= since)
      );
    });
  }, [agency]);
  return vehicles;
}

function useHydrantsNear(agency, center) {
  const [hydrants, setHydrants] = useState([]);
  const lat = center?.lat;
  const lng = center?.lng;
  useEffect(() => {
    if (!agency || !Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;
    let active = true;
    const HALF = 0.05; // ~0.1° box, as on the legacy board
    loadHydrants(
      { bounds: { north: lat + HALF, south: lat - HALF, east: lng + HALF, west: lng - HALF } },
      { agencies: [agency] }
    )
      .then(({ pois }) => active && setHydrants(pois.map((p) => p.poi)))
      .catch((err) => active && console.error("Hydrant load failed", err));
    return () => {
      active = false;
    };
  }, [agency, lat, lng]);
  return useMemo(
    () => (Number.isFinite(lat) && Number.isFinite(lng) ? nearestHydrants(hydrants, { lat, lng }) : []),
    [hydrants, lat, lng]
  );
}

// Google engine, loaded only when a board uses it (or Mapbox fails).
function GoogleIncidentMap({ center, zoom, layers }) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: [],
    id: "google-map-script",
  });
  if (loadError) return <div style={{ ...FILL, color: "white", padding: 20 }}>Error loading maps</div>;
  if (!isLoaded) return <div style={{ ...FILL, color: "white", padding: 20 }}>Loading Map...</div>;
  return (
    <GoogleMap mapContainerStyle={FILL} center={center} zoom={zoom} options={GOOGLE_INCIDENT_OPTIONS}>
      <TrafficLayer autoRefresh />
      <GoogleIncidentLayers {...layers} />
    </GoogleMap>
  );
}

// The incident map, laid out exactly like the legacy board's full-screen alarm
// map (shared components in components/map/incidentMapParts): satellite streets
// at street level on the current alarm, cycling through active alarms every
// 12 seconds, call-type pins, hydrants, live unit locations, the caller's
// location from CAD comments, and the OPEN ALARM / Personnel Responding cards.
// Used as a board widget and full-screen for alarm takeover. With no active
// alarm it shows the station area.
const AlarmMapWidget = ({ config = {}, agency, board }) => {
  const rawAlarms = useAlarms(agency);
  const info = useAgencyInfo(agency);
  const demo = isDemoData();

  const station = useMemo(() => {
    const cfg = board?.dataSources?.coordinates;
    const lat = coord(cfg?.latitude, info.lat);
    const lng = coord(cfg?.longitude, info.lng);
    return hasValidCoords(lat, lng) ? { lat, lng } : null;
  }, [board, info.lat, info.lng]);

  const alarms = useMemo(
    () =>
      rawAlarms.map((a) => ({
        ...a,
        lat: coord(a.Latitude, a.latitude, a.lat),
        lng: coord(a.Longitude, a.longitude, a.lng, a.long),
      })),
    [rawAlarms]
  );

  // Cycle through active alarms like the legacy board.
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (alarms.length < 2) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % alarms.length), ALARM_CYCLE_MS);
    return () => clearInterval(t);
  }, [alarms.length]);
  const current = alarms.length ? alarms[index % alarms.length] : null;

  const callins = useCallins(agency, current?.id);
  const vehicles = useUnitLocations(agency);
  const callerLocation = useMemo(() => extractCallerCoordinates(current?.EventComments), [current?.EventComments]);
  const currentHasCoords = !!current && hasValidCoords(current.lat, current.lng);
  const focusLat = currentHasCoords ? current.lat : station?.lat;
  const focusLng = currentHasCoords ? current.lng : station?.lng;
  const focus = useMemo(
    () => (Number.isFinite(focusLat) && Number.isFinite(focusLng) ? { lat: focusLat, lng: focusLng } : null),
    [focusLat, focusLng]
  );
  const hydrants = useHydrantsNear(agency, focus);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const provider = config.mapProvider || board?.dataSources?.mapProvider || "mapbox";
  const [mapboxError, setMapboxError] = useState(false);
  const useGoogle = !demo && (provider === "google" || (mapboxError && !!process.env.REACT_APP_GOOGLE_MAPS_API_KEY));

  // Fly to the alarm being shown (or back to the station when clear).
  // The map is created asynchronously, so this also runs once it has loaded.
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const zoom = current ? MAPBOX_INCIDENT_ZOOM : IDLE_ZOOM;
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !focus) return;
    mapRef.current.flyTo({ center: [focus.lng, focus.lat], zoom, duration: 1000, essential: true });
  }, [mapLoaded, focus, zoom]);

  const layers = { alarms, hydrants, vehicles, callerLocation, selectedVehicle, onSelectVehicle: setSelectedVehicle };
  const text = current ? { ...current, Callins: callins } : null;
  const token = demo ? DEMO_MAP_TOKEN : MAPBOX_TOKEN;

  let map;
  if (current && !currentHasCoords) {
    map = <NoMappingScreen logoUrl={info.logo} style={FILL} />;
  } else if (!focus) {
    map = <div className="sb-alarmmap-nomap" />;
  } else if (useGoogle) {
    map = <GoogleIncidentMap center={focus} zoom={current ? GOOGLE_INCIDENT_ZOOM : IDLE_ZOOM} layers={layers} />;
  } else if (token) {
    map = (
      <Map
        ref={mapRef}
        mapboxAccessToken={token}
        initialViewState={{ latitude: focus.lat, longitude: focus.lng, zoom }}
        mapStyle={demo ? demoMapStyle() : MAPBOX_INCIDENT_STYLE}
        style={FILL}
        onLoad={() => setMapLoaded(true)}
        onError={() => setMapboxError(true)}
      >
        <MapboxIncidentLayers {...layers} />
        {!current && station && (
          <Marker latitude={station.lat} longitude={station.lng}>
            <div className="sb-station-marker" />
          </Marker>
        )}
      </Map>
    );
  } else {
    map = <div className="sb-alarmmap-nomap" />;
  }

  return (
    <div className="sb-alarmmap legacy">
      <MapAnimations />
      {map}
      {current ? (
        <FloatingMapCard text={text} callId={current.id} agency={agency} isFullscreenMapVisible contained />
      ) : (
        <div className="sb-alarmmap-idle">No active incidents</div>
      )}
    </div>
  );
};

export default AlarmMapWidget;
